import React, { useMemo } from 'react';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { AxiosResponse } from 'axios';
import { useQuery } from 'react-query';
import { platformAPI } from '../../services/api';
import {
  buildTenantBaseUrl,
  SupportTenantOption,
} from '../../utils/supportTenantAccess';

function SupportTenantPortal() {
  const { data: companies = [], isLoading } = useQuery<AxiosResponse<any>, Error, SupportTenantOption[]>(
    ['support-tenant-portal-companies'],
    () => platformAPI.getSupportTenantSwitchCompanies(),
    {
      select: (response) => {
        const data = response.data;
        if (Array.isArray(data)) return data as SupportTenantOption[];
        if (Array.isArray(data?.companies)) return data.companies as SupportTenantOption[];
        return [] as SupportTenantOption[];
      },
      staleTime: 60_000,
    },
  );

  const activeCompanies = useMemo(
    () => companies.filter((company) => company?.slug && company?.status !== 'disabled'),
    [companies],
  );

  const openTenant = (slug: string) => {
    const targetBaseUrl = buildTenantBaseUrl(slug);
    const token = window.localStorage.getItem('token');
    const handoffUrl = new URL('/support/handoff', targetBaseUrl);
    handoffUrl.searchParams.set('next', '/admin');
    if (token) {
      handoffUrl.searchParams.set('token', token);
    }
    window.location.assign(handoffUrl.toString());
  };

  return (
    <div className="mx-auto max-w-5xl px-page-gutter py-10">
      <div className="rounded-card bg-surface-container-lowest p-6 shadow-ambient">
        <h1 className="text-2xl font-bold text-on-surface">Support Tenant Portal</h1>
        <p className="mt-2 text-on-surface-variant">
          Select a company tenant to manage on behalf of the platform.
        </p>
      </div>

      <div className="mt-6 rounded-card bg-surface-container-lowest p-6 shadow-ambient">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-on-surface">Available Companies</h2>
          {isLoading && <span className="text-sm text-on-surface-variant">Loading...</span>}
        </div>

        {!isLoading && activeCompanies.length === 0 && (
          <div className="rounded-xl border border-outline/60 bg-surface-container-low p-4 text-sm text-on-surface-variant">
            No active companies available.
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {activeCompanies.map((company) => (
            <button
              key={company.id}
              type="button"
              onClick={() => openTenant(company.slug)}
              className="flex items-center justify-between rounded-xl border border-outline/60 bg-surface px-4 py-3 text-left hover:bg-surface-container-low"
            >
              <span>
                <span className="block text-sm font-semibold text-on-surface">{company.name || company.slug}</span>
                <span className="block text-xs text-on-surface-variant">{company.slug}</span>
              </span>
              <BuildingOfficeIcon className="h-5 w-5 text-on-surface-variant" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SupportTenantPortal;
